/**
 * Funções para importação de Workspace CRM
 * 
 * Faz parse e validação de arquivos .uaizyworkspace, e cria
 * todas as entidades no tenant destino respeitando a ordem de
 * dependências (FKs).
 * 
 * RELEASE: 1.0.0 - 2026-03-22 - Fase 2: Importação completa
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type {
  WorkspaceExportFormat,
  WorkspaceExportModules,
  ExportedPipeline,
  ExportedProductCategory,
  ExportedProduct,
  ExportedPriceTable,
  ExportedContract,
  ExportedSchedulingEventType,
  ExportedKanbanCategory,
} from './workspace-types';
import { WORKSPACE_EXPORT_FORMAT_VERSION } from './workspace-types';

/** Logger para módulo de importação */
const log = createLogger('WorkspaceImport');

// =========================================================================
// Tipos internos de importação
// =========================================================================

/**
 * Resultado da validação do arquivo importado
 */
export interface ImportValidationResult {
  /** Se o arquivo é válido */
  valid: boolean;
  /** Mensagens de erro (se inválido) */
  errors: string[];
  /** Dados parseados (se válido) */
  data: WorkspaceExportFormat | null;
}

/**
 * Progresso da importação por módulo
 */
export interface ImportProgress {
  /** Etapa atual */
  currentStep: string;
  /** Número do passo atual */
  stepNumber: number;
  /** Total de passos */
  totalSteps: number;
  /** Se já finalizou */
  completed: boolean;
  /** Erro fatal (se houver) */
  error: string | null;
}

/**
 * Resultado final da importação
 */
export interface ImportResult {
  /** Se a importação foi bem-sucedida */
  success: boolean;
  /** ID do workspace criado */
  workspaceId: string | null;
  /** Resumo do que foi criado */
  summary: {
    pipelines: number;
    phases: number;
    product_categories: number;
    products: number;
    price_tables: number;
    contracts: number;
    scheduling_event_types: number;
    flows: number;
  };
  /** Erros não fatais (entidades puladas, etc.) */
  warnings: string[];
  /** Mapas de referências internas para uso na importação de fluxos (Fase 3) */
  internalMaps: {
    workspaceId: string;
    workspaceName: string;
    pipelines: Record<string, string>;
    phases: Record<string, string>;
    categories: Record<string, string>;
    products: Record<string, string>;
  } | null;
}

// =========================================================================
// Parse e validação
// =========================================================================

/**
 * Faz parse de um arquivo .uaizyworkspace e valida estrutura
 * 
 * @param file - Arquivo carregado pelo usuário
 * @returns Resultado da validação com dados parseados
 */
export async function parseWorkspaceFile(file: File): Promise<ImportValidationResult> {
  const errors: string[] = [];

  // Validar extensão
  if (!file.name.endsWith('.uaizyworkspace')) {
    errors.push('Arquivo deve ter extensão .uaizyworkspace');
    return { valid: false, errors, data: null };
  }

  // Validar tamanho (máx 50MB)
  if (file.size > 50 * 1024 * 1024) {
    errors.push('Arquivo muito grande (máximo 50MB)');
    return { valid: false, errors, data: null };
  }

  // Ler conteúdo
  let content: string;
  try {
    content = await file.text();
  } catch {
    errors.push('Não foi possível ler o arquivo');
    return { valid: false, errors, data: null };
  }

  // Parse JSON
  let data: WorkspaceExportFormat;
  try {
    data = JSON.parse(content);
  } catch {
    errors.push('Arquivo não contém JSON válido');
    return { valid: false, errors, data: null };
  }

  // Validar campos obrigatórios
  if (!data.format_version) {
    errors.push('Campo format_version ausente');
  }
  if (data.source_app !== 'UaizyChat') {
    errors.push('Arquivo não foi gerado pelo UaizyChat');
  }
  if (!data.workspace) {
    errors.push('Dados do workspace ausentes');
  }
  if (!data.workspace?.name) {
    errors.push('Nome do workspace ausente');
  }
  if (!data.workspace?.category_slug) {
    errors.push('Categoria do workspace ausente');
  }
  if (!data.exported_modules) {
    errors.push('Módulos exportados não informados');
  }

  // Validar versão do formato
  if (data.format_version && data.format_version !== WORKSPACE_EXPORT_FORMAT_VERSION) {
    errors.push(`Versão do formato incompatível: ${data.format_version} (esperada: ${WORKSPACE_EXPORT_FORMAT_VERSION})`);
  }

  if (errors.length > 0) {
    return { valid: false, errors, data: null };
  }

  log.info('Arquivo parseado com sucesso', {
    workspace: data.workspace?.name,
    modules: data.exported_modules,
  });

  return { valid: true, errors: [], data };
}

// =========================================================================
// Importação principal
// =========================================================================

/**
 * Importa workspace completo no tenant destino
 * 
 * Cria entidades na ordem correta respeitando dependências:
 * 1. Resolver category_id via slug
 * 2. Criar workspace
 * 3. Pipelines → Fases
 * 4. Categorias de produto (hierarquia parent)
 * 5. Produtos → product_workspaces
 * 6. Tabelas de preço → price_table_workspaces + items
 * 7. Contratos (templates → published)
 * 8. Agendamento (event types → availability)
 * 
 * @param data - Dados do arquivo importado
 * @param modules - Módulos selecionados para importar
 * @param workspaceName - Nome do workspace (pode ser editado pelo usuário)
 * @param tenantId - ID do tenant destino
 * @param onProgress - Callback de progresso
 * @returns Resultado da importação
 */
export async function importWorkspace(
  data: WorkspaceExportFormat,
  modules: WorkspaceExportModules,
  workspaceName: string,
  tenantId: string,
  userId: string,
  onProgress?: (progress: ImportProgress) => void,
): Promise<ImportResult> {
  const warnings: string[] = [];
  const summary = {
    pipelines: 0,
    phases: 0,
    product_categories: 0,
    products: 0,
    price_tables: 0,
    contracts: 0,
    scheduling_event_types: 0,
    flows: 0,
  };

  // Contabilizar total de passos baseado nos módulos selecionados
  let totalSteps = 2; // workspace + categoria
  if (modules.pipelines) totalSteps++;
  if (modules.product_categories) totalSteps++;
  if (modules.products) totalSteps++;
  if (modules.price_tables) totalSteps++;
  if (modules.contracts) totalSteps++;
  if (modules.scheduling) totalSteps++;
  if (modules.conversation_kanban) totalSteps++;

  let stepNumber = 0;

  const reportProgress = (step: string) => {
    stepNumber++;
    onProgress?.({
      currentStep: step,
      stepNumber,
      totalSteps,
      completed: false,
      error: null,
    });
  };

  try {
    // =====================================================================
    // PASSO 1: Resolver category_id via slug
    // =====================================================================
    reportProgress('Resolvendo categoria do workspace...');

    const { data: category, error: catError } = await supabase
      .from('crm_workspace_categories')
      .select('id')
      .eq('slug', data.workspace.category_slug)
      .eq('is_active', true)
      .maybeSingle();

    if (catError) {
      log.error('Erro ao buscar categoria', catError);
      throw new Error(`Erro ao resolver categoria: ${catError.message}`);
    }

    if (!category) {
      // Usar primeira categoria disponível como fallback
      const { data: fallbackCat } = await supabase
        .from('crm_workspace_categories')
        .select('id')
        .eq('is_active', true)
        .order('sort_order')
        .limit(1)
        .maybeSingle();

      if (!fallbackCat) {
        throw new Error('Nenhuma categoria de workspace disponível no sistema');
      }

      warnings.push(`Categoria "${data.workspace.category_slug}" não encontrada. Usando categoria padrão.`);
      category!; // TypeScript: usaremos fallbackCat
      var categoryId = fallbackCat.id;
    } else {
      var categoryId = category.id;
    }

    // =====================================================================
    // PASSO 2: Criar workspace
    // =====================================================================
    reportProgress('Criando workspace...');

    const { data: newWorkspace, error: wsError } = await supabase
      .from('crm_workspaces')
      .insert({
        tenant_id: tenantId,
        name: workspaceName.trim(),
        description: data.workspace.description,
        color: data.workspace.color || '#3B82F6',
        category_id: categoryId,
        auto_distribution_enabled: data.workspace.auto_distribution_enabled ?? false,
      })
      .select('id')
      .single();

    if (wsError) {
      log.error('Erro ao criar workspace', wsError);
      throw new Error(`Erro ao criar workspace: ${wsError.message}`);
    }

    const workspaceId = newWorkspace.id;
    log.info('Workspace criado', { workspaceId, name: workspaceName });

    // Mapas de referência para resolver dependências entre módulos
    /** Mapa: nome do pipeline → id criado */
    const pipelineNameToId: Record<string, string> = {};
    /** Mapa: "pipelineName::phaseName" → id da fase criada */
    const phaseNameToId: Record<string, string> = {};
    /** Mapa: nome da categoria → id criado */
    const categoryNameToId: Record<string, string> = {};
    /** Mapa: nome do produto → id criado */
    const productNameToId: Record<string, string> = {};

    // =====================================================================
    // PASSO 3: Criar Pipelines e Fases
    // =====================================================================
    if (modules.pipelines && data.pipelines?.length > 0) {
      reportProgress('Criando pipelines e fases...');

      for (const pipeline of data.pipelines) {
        // Criar pipeline
        const { data: newPipeline, error: pipError } = await supabase
          .from('crm_pipelines')
          .insert({
            tenant_id: tenantId,
            workspace_id: workspaceId,
            name: pipeline.name,
            description: pipeline.description,
            is_default: pipeline.is_default,
            sort_order: pipeline.sort_order,
            is_active: true,
          })
          .select('id')
          .single();

        if (pipError) {
          warnings.push(`Erro ao criar pipeline "${pipeline.name}": ${pipError.message}`);
          continue;
        }

        pipelineNameToId[pipeline.name] = newPipeline.id;
        summary.pipelines++;

        // Criar fases do pipeline
        if (pipeline.phases?.length > 0) {
          const phasesInsert = pipeline.phases.map(phase => ({
            pipeline_id: newPipeline.id,
            tenant_id: tenantId,
            name: phase.name,
            color: phase.color,
            sort_order: phase.sort_order,
            is_won: phase.is_won ?? false,
            is_lost: phase.is_lost ?? false,
            probability_percent: phase.probability_percent,
            auto_move_days: phase.auto_move_days,
            description: phase.description,
            icon: phase.icon,
          }));

          const { data: newPhases, error: phError } = await supabase
            .from('crm_pipeline_phases')
            .insert(phasesInsert)
            .select('id, name');

          if (phError) {
            warnings.push(`Erro ao criar fases do pipeline "${pipeline.name}": ${phError.message}`);
          } else {
            summary.phases += (newPhases?.length || 0);
            // Mapear fases para referência do scheduling
            (newPhases || []).forEach((ph: any) => {
              phaseNameToId[`${pipeline.name}::${ph.name}`] = ph.id;
            });
          }
        }
      }
    }

    // =====================================================================
    // PASSO 4: Criar Categorias de Produto
    // =====================================================================
    if (modules.product_categories && data.product_categories?.length > 0) {
      reportProgress('Criando categorias de produto...');

      // Ordenar: primeiro as sem parent, depois as com parent
      const sorted = [...data.product_categories].sort((a, b) => {
        if (!a.parent_name && b.parent_name) return -1;
        if (a.parent_name && !b.parent_name) return 1;
        return 0;
      });

      for (const cat of sorted) {
        // Resolver parent_id se existir
        let parentId: string | null = null;
        if (cat.parent_name) {
          // Primeiro verificar se já criamos nesta importação
          parentId = categoryNameToId[cat.parent_name] || null;

          // Se não, buscar no tenant existente
          if (!parentId) {
            const { data: existingParent } = await supabase
              .from('crm_product_categories')
              .select('id')
              .eq('tenant_id', tenantId)
              .eq('name', cat.parent_name)
              .maybeSingle();

            parentId = existingParent?.id || null;
          }

          if (!parentId) {
            warnings.push(`Categoria pai "${cat.parent_name}" não encontrada para "${cat.name}". Criada sem pai.`);
          }
        }

        // Verificar se já existe categoria com mesmo nome no tenant
        const { data: existing } = await supabase
          .from('crm_product_categories')
          .select('id')
          .eq('tenant_id', tenantId)
          .eq('name', cat.name)
          .maybeSingle();

        if (existing) {
          // Usar existente
          categoryNameToId[cat.name] = existing.id;
          warnings.push(`Categoria "${cat.name}" já existe. Usando existente.`);
          continue;
        }

        const { data: newCat, error: catInsertError } = await supabase
          .from('crm_product_categories')
          .insert({
            tenant_id: tenantId,
            name: cat.name,
            color: cat.color,
            icon: cat.icon,
            description: cat.description,
            sort_order: cat.sort_order,
            parent_id: parentId,
          })
          .select('id')
          .single();

        if (catInsertError) {
          warnings.push(`Erro ao criar categoria "${cat.name}": ${catInsertError.message}`);
          continue;
        }

        categoryNameToId[cat.name] = newCat.id;
        summary.product_categories++;
      }
    }

    // =====================================================================
    // PASSO 5: Criar Produtos + vincular ao workspace
    // =====================================================================
    if (modules.products && data.products?.length > 0) {
      reportProgress('Criando produtos...');

      for (const product of data.products) {
        // Resolver category_id pelo nome
        let prodCategoryId: string | null = null;
        if (product.category_name) {
          prodCategoryId = categoryNameToId[product.category_name] || null;

          // Se não foi criada nesta importação, buscar existente no tenant
          if (!prodCategoryId) {
            const { data: existingCat } = await supabase
              .from('crm_product_categories')
              .select('id')
              .eq('tenant_id', tenantId)
              .eq('name', product.category_name)
              .maybeSingle();

            prodCategoryId = existingCat?.id || null;
          }
        }

        // Verificar duplicata por nome
        const { data: existingProd } = await supabase
          .from('crm_products')
          .select('id')
          .eq('tenant_id', tenantId)
          .eq('name', product.name)
          .eq('is_active', true)
          .maybeSingle();

        if (existingProd) {
          productNameToId[product.name] = existingProd.id;
          warnings.push(`Produto "${product.name}" já existe. Usando existente.`);

          // Vincular ao workspace mesmo assim
          await supabase
            .from('crm_product_workspaces')
            .upsert({
              product_id: existingProd.id,
              workspace_id: workspaceId,
              tenant_id: tenantId,
            }, { onConflict: 'product_id,workspace_id' })
            .select();

          continue;
        }

        const { data: newProduct, error: prodError } = await supabase
          .from('crm_products')
          .insert({
            tenant_id: tenantId,
            name: product.name,
            sku: product.sku,
            description: product.description,
            default_price: product.default_price,
            cost_price: product.cost_price,
            currency: product.currency ?? 'BRL',
            unit_type: product.unit_type ?? 'unit',
            product_type: product.product_type ?? 'product',
            category_id: prodCategoryId,
            custom_fields: product.custom_fields,
            is_active: true,
          } as any)
          .select('id')
          .single();

        if (prodError) {
          warnings.push(`Erro ao criar produto "${product.name}": ${prodError.message}`);
          continue;
        }

        productNameToId[product.name] = newProduct.id;
        summary.products++;

        // Vincular produto ao workspace
        await supabase
          .from('crm_product_workspaces')
          .insert({
            product_id: newProduct.id,
            workspace_id: workspaceId,
            tenant_id: tenantId,
          });
      }
    }

    // =====================================================================
    // PASSO 6: Criar Tabelas de Preço + itens + vincular ao workspace
    // =====================================================================
    if (modules.price_tables && data.price_tables?.length > 0) {
      reportProgress('Criando tabelas de preço...');

      for (const table of data.price_tables) {
        const { data: newTable, error: tableError } = await supabase
          .from('crm_price_tables')
          .insert({
            tenant_id: tenantId,
            name: table.name,
            description: table.description,
            currency: table.currency ?? 'BRL',
            is_default: false, // Nunca importar como default (evita conflito)
            conditions: table.conditions,
            is_active: true,
          })
          .select('id')
          .single();

        if (tableError) {
          warnings.push(`Erro ao criar tabela de preço "${table.name}": ${tableError.message}`);
          continue;
        }

        summary.price_tables++;

        // Vincular tabela ao workspace
        await supabase
          .from('crm_price_table_workspaces')
          .insert({
            price_table_id: newTable.id,
            workspace_id: workspaceId,
            tenant_id: tenantId,
          });

        // Criar itens da tabela
        if (table.items?.length > 0) {
          for (const item of table.items) {
            // Resolver product_id pelo nome
            let productId = productNameToId[item.product_name] || null;

            if (!productId) {
              // Buscar produto existente no tenant
              const { data: existingProd } = await supabase
                .from('crm_products')
                .select('id')
                .eq('tenant_id', tenantId)
                .eq('name', item.product_name)
                .eq('is_active', true)
                .maybeSingle();

              productId = existingProd?.id || null;
            }

            if (!productId) {
              warnings.push(`Produto "${item.product_name}" não encontrado para item da tabela "${table.name}". Item pulado.`);
              continue;
            }

            await supabase
              .from('crm_price_table_items')
              .insert({
                price_table_id: newTable.id,
                product_id: productId,
                price: item.price,
                min_quantity: item.min_quantity ?? 1,
                discount_percent: item.discount_percent,
                discount_fixed: item.discount_fixed,
                tenant_id: tenantId,
              });
          }
        }
      }
    }

    // =====================================================================
    // PASSO 7: Criar Contratos (templates → published)
    // =====================================================================
    if (modules.contracts && data.contracts?.length > 0) {
      reportProgress('Criando contratos...');

      for (const contract of data.contracts) {
        // Criar template
        const { data: newTemplate, error: tplError } = await supabase
          .from('contract_templates')
          .insert({
            tenant_id: tenantId,
            name: contract.template.name,
            description: contract.template.description,
            category: contract.template.category || 'general',
            content_html: contract.template.content_html || '',
            variables: contract.template.variables || [],
            allowed_delivery_methods: contract.template.allowed_delivery_methods || ['email'],
            require_selfie: contract.template.require_selfie ?? false,
            show_page_numbers: contract.template.show_page_numbers ?? false,
            page_number_position: contract.template.page_number_position || 'bottom-right',
            bg_margin_top_mm: contract.template.bg_margin_top_mm ?? 33,
            bg_margin_bottom_mm: contract.template.bg_margin_bottom_mm ?? 20,
            pagination_y_offset_mm: contract.template.pagination_y_offset_mm ?? 12,
            base_pdf_position: 'before',
            is_active: true,
          } as any)
          .select('id')
          .single();

        if (tplError) {
          warnings.push(`Erro ao criar template "${contract.template.name}": ${tplError.message}`);
          continue;
        }

        // Criar versão do template
        await supabase
          .from('contract_template_versions')
          .insert({
            template_id: newTemplate.id,
            tenant_id: tenantId,
            content_html: contract.template.content_html,
            variables: contract.template.variables || {},
            show_page_numbers: contract.template.show_page_numbers ?? false,
            page_number_position: contract.template.page_number_position ?? 'bottom-center',
            version_number: 1,
            created_by: null, // Importação não tem user_id
          });

        // Criar contrato publicado (se existir no export)
        // published_contracts NÃO tem is_active — removido
        if (contract.published) {
          const { error: pubInsertError } = await supabase
            .from('published_contracts')
            .insert({
              tenant_id: tenantId,
              template_id: newTemplate.id,
              name: contract.published.name,
              description: contract.published.description,
              status: 'draft', // Sempre criar como draft por segurança
              default_signers: contract.published.default_signers || [],
              allowed_delivery_methods: contract.published.allowed_delivery_methods || [],
              require_selfie: contract.published.require_selfie ?? false,
              workspace_ids: [workspaceId], // Vincular ao novo workspace
            });

          if (pubInsertError) {
            warnings.push(`Erro ao criar contrato publicado "${contract.published.name}": ${pubInsertError.message}`);
          }
        }

        summary.contracts++;
      }
    }

    // =====================================================================
    // PASSO 8: Criar Agendamento (event types → availability)
    // =====================================================================
    if (modules.scheduling && data.scheduling_event_types?.length > 0) {
      reportProgress('Criando tipos de agendamento...');

      for (const eventType of data.scheduling_event_types) {
        // Resolver pipeline_id e phase_id por nome
        let pipelineId: string | null = null;
        let phaseId: string | null = null;

        if (eventType.crm_pipeline_name) {
          pipelineId = pipelineNameToId[eventType.crm_pipeline_name] || null;

          // Se não foi criado nesta importação, buscar existente
          if (!pipelineId) {
            const { data: existingPipeline } = await supabase
              .from('crm_pipelines')
              .select('id')
              .eq('tenant_id', tenantId)
              .eq('workspace_id', workspaceId)
              .eq('name', eventType.crm_pipeline_name)
              .eq('is_active', true)
              .maybeSingle();

            pipelineId = existingPipeline?.id || null;
          }
        }

        if (eventType.crm_phase_name && eventType.crm_pipeline_name) {
          const phaseKey = `${eventType.crm_pipeline_name}::${eventType.crm_phase_name}`;
          phaseId = phaseNameToId[phaseKey] || null;

          // Se não foi criado, buscar existente
          if (!phaseId && pipelineId) {
            const { data: existingPhase } = await supabase
              .from('crm_pipeline_phases')
              .select('id')
              .eq('pipeline_id', pipelineId)
              .eq('name', eventType.crm_phase_name)
              .maybeSingle();

            phaseId = existingPhase?.id || null;
          }
        }

        // Gerar slug único
        const uniqueSlug = `${eventType.slug}-${Date.now()}`;

        const { data: newEventType, error: etError } = await supabase
          .from('scheduling_event_types')
          .insert({
            tenant_id: tenantId,
            user_id: userId,
            crm_workspace_id: workspaceId,
            title: eventType.title,
            slug: uniqueSlug,
            description: eventType.description,
            duration_minutes: eventType.duration_minutes,
            buffer_before_minutes: eventType.buffer_before_minutes ?? 0,
            buffer_after_minutes: eventType.buffer_after_minutes ?? 0,
            min_notice_hours: eventType.min_notice_hours ?? 1,
            max_advance_days: eventType.max_advance_days ?? 30,
            timezone: eventType.timezone ?? 'America/Sao_Paulo',
            color: eventType.color,
            confirmation_message: eventType.confirmation_message,
            custom_questions: eventType.custom_questions,
            crm_auto_create_deal: eventType.crm_auto_create_deal ?? false,
            crm_source: eventType.crm_source,
            crm_seller_assignment_mode: eventType.crm_seller_assignment_mode ?? 'none',
            crm_pipeline_id: pipelineId,
            crm_phase_id: phaseId,
            is_active: true,
          })
          .select('id')
          .single();

        if (etError) {
          warnings.push(`Erro ao criar tipo de agendamento "${eventType.title}": ${etError.message}`);
          continue;
        }

        summary.scheduling_event_types++;

        // Criar disponibilidades
        // scheduling_availability: is_available (NÃO is_active), slot_interval_minutes
        if (eventType.availability?.length > 0) {
          const availInsert = eventType.availability.map(a => ({
            event_type_id: newEventType.id,
            day_of_week: a.day_of_week,
            start_time: a.start_time,
            end_time: a.end_time,
            is_available: a.is_available ?? true,
            slot_interval_minutes: a.slot_interval_minutes ?? 30,
          }));

          const { error: availError } = await supabase
            .from('scheduling_availability')
            .insert(availInsert);

          if (availError) {
            warnings.push(`Erro ao criar disponibilidades do agendamento "${eventType.title}": ${availError.message}`);
          }
        }
      }
    }

    // =====================================================================
    // PASSO 9: Criar Kanban de Conversas (independente do workspace)
    // =====================================================================
    if (modules.conversation_kanban && data.kanban_categories?.length > 0) {
      reportProgress('Criando categorias de Kanban de conversas...');

      for (const category of data.kanban_categories) {
        // Verificar se já existe categoria com mesmo nome no tenant
        const { data: existingCat } = await supabase
          .from('kanban_categories')
          .select('id')
          .eq('tenant_id', tenantId)
          .eq('name', category.name)
          .maybeSingle();

        if (existingCat) {
          warnings.push(`Categoria Kanban "${category.name}" já existe. Pulada.`);

          // Adicionar fases que não existem na categoria existente
          if (category.phases?.length > 0) {
            for (const phase of category.phases) {
              const { data: existingPhase } = await supabase
                .from('kanban_phases')
                .select('id')
                .eq('category_id', existingCat.id)
                .eq('name', phase.name)
                .eq('tenant_id', tenantId)
                .maybeSingle();

              if (!existingPhase) {
                const { error: phInsErr } = await supabase
                  .from('kanban_phases')
                  .insert({
                    category_id: existingCat.id,
                    tenant_id: tenantId,
                    name: phase.name,
                    color: phase.color,
                    position: phase.position ?? 0,
                    is_default: false, // Não importar como default
                    is_final: phase.is_final ?? false,
                  });
                if (phInsErr) {
                  warnings.push(`Erro ao criar fase Kanban "${phase.name}": ${phInsErr.message}`);
                }
              }
            }
          }
          continue;
        }

        // Criar nova categoria
        const { data: newCat, error: catErr } = await supabase
          .from('kanban_categories')
          .insert({
            tenant_id: tenantId,
            name: category.name,
            color: category.color,
            description: category.description,
            position: category.position ?? 0,
            is_default: false, // Não importar como default
            is_active: true,
          })
          .select('id')
          .single();

        if (catErr) {
          warnings.push(`Erro ao criar categoria Kanban "${category.name}": ${catErr.message}`);
          continue;
        }

        // Criar fases da categoria
        if (category.phases?.length > 0) {
          const phasesInsert = category.phases.map(phase => ({
            category_id: newCat.id,
            tenant_id: tenantId,
            name: phase.name,
            color: phase.color,
            position: phase.position ?? 0,
            is_default: phase.is_default ?? false,
            is_final: phase.is_final ?? false,
          }));

          const { error: phErr } = await supabase
            .from('kanban_phases')
            .insert(phasesInsert);

          if (phErr) {
            warnings.push(`Erro ao criar fases da categoria Kanban "${category.name}": ${phErr.message}`);
          }
        }
      }
    }

    // Fluxos serão tratados na Fase 3 (wizard de mapeamento)
    // Fluxos são importados separadamente via ImportWorkspaceFlowMapping (Fase 3)

    // Progresso final
    onProgress?.({
      currentStep: 'Importação concluída!',
      stepNumber: totalSteps,
      totalSteps,
      completed: true,
      error: null,
    });

    log.info('Importação concluída com sucesso', { workspaceId, summary, warnings: warnings.length });

    return {
      success: true,
      workspaceId,
      summary,
      warnings,
      internalMaps: {
        workspaceId,
        workspaceName: workspaceName.trim(),
        pipelines: pipelineNameToId,
        phases: phaseNameToId,
        categories: categoryNameToId,
        products: productNameToId,
      },
    };

  } catch (error: any) {
    log.error('Erro fatal na importação', error);

    onProgress?.({
      currentStep: 'Erro na importação',
      stepNumber: stepNumber,
      totalSteps,
      completed: true,
      error: error.message,
    });

    return {
      success: false,
      workspaceId: null,
      summary,
      warnings: [...warnings, `Erro fatal: ${error.message}`],
      internalMaps: null,
    };
  }
}
